/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */
import {
  toggleMenu,
  highlightNavbarSection,
  changeRoute
} from '../containers/Nav/actions';
import { selectTimeslot } from '../containers/Schedule/actions';
import { dispatch } from '../root/store';

import UpcomingIcon from '../assets/icons/nav_bar/schedule_visit.svg';
import CurrentIcon from '../assets/icons/nav_bar/current_report.svg';
import HistoryIcon from '../assets/icons/nav_bar/past_visits.svg';
import DemographicsIcon from '../assets/icons/nav_bar/demographics.svg';
import BodyCompositionIcon from '../assets/icons/nav_bar/body_composition.svg';
import BodySymmetryIcon from '../assets/icons/nav_bar/body_symmetry.svg';
import CognitionIcon from '../assets/icons/nav_bar/cognition.svg';
import LaboratoryIcon from '../assets/icons/nav_bar/laboratory.svg';
import DexterityIcon from '../assets/icons/nav_bar/dexterity.svg';
import PhysicalActivityIcon from '../assets/icons/nav_bar/physical_activity.svg';
import StrengthIcon from '../assets/icons/nav_bar/strength.svg';
import BalanceIcon from '../assets/icons/nav_bar/balance.svg';
import AnthropometricsIcon from '../assets/icons/nav_bar/anthropometrics.svg';
import NutritionIcon from '../assets/icons/nav_bar/nutrition.svg';

import LogoutIcon from '../assets/icons/mobile_logout.svg';
import ConsentIcon from '../assets/icons/nav_bar/consent_yes.svg';
import ProfileIcon from '../assets/icons/profile_icon.svg';
import DownCarot from '../assets/icons/carat.svg';
import LogoWhite from '../assets/icons/logo-white.svg';

export const app = {
  title: 'Thorne HealthTech'
};

export const payment = {
  feeNotice:
    '* A $30 fee will be charged for cancellations or rescheduling within 48 hours of appointment time'
};

export const reports = [
  'Demographics',
  'Body Composition',
  'Body Symmetry',
  'Cognition',
  'Laboratory',
  'Dexterity',
  'Physical Activity',
  'Strength',
  'Balance',
  'Anthropometrics',
  'Nutrition'
];

export const assessments = [
  // 'Consent',
  'Physical Activity',
  'Nutrition',
  'Medical History',
  'Medications',
  'Family History',
  'Social History',
  'Sleep',
  'Mental Health'
];

export const terms = {
  agreement: `        Lab100 is not a replacement for medical care. Lab100 is
                      focused on assessing health, not on screening for,
                      diagnosing, or treating disease. By providing a
                      comprehensive health assessment, Lab100 provides a
                      complement to, not a replacement for, high quality primary
                      care. By proceeding to make an appointment with Lab100 you
                      acknowledge that the assessment provided by Lab100 is not
                      medical diagnosis or care and is not a substitute for a
                      doctor’s examination and care.
                      <br />
                      <br />
                      You acknowledge that you are solely responsible for
                      contacting your physician for attention to any specific
                      medical needs that may be indicated by the information
                      that Lab100 provides to you. You will not hold Lab100
                      responsible for any action or failure to act on the
                      information derived from the health assessment it will
                      perform. Please check the box below to acknowledge that
                      you understand and accept these terms of service.
                `
};

export const header = {
  img: {
    src: LogoWhite
  },
  onClick: () => dispatch(changeRoute({ route: '/' }))
};

export const desktopHeader = {
  img: {
    src: LogoWhite
  },
  isForMobile: true,
  onClick: () => dispatch(changeRoute({ route: '/' }))
};

export const mobileHeader = {
  img: {
    src: LogoWhite
  },
  onClick: () => dispatch(changeRoute({ route: '/' }))
};

export const footer = {
  contact: {
    msg: 'Have a Question?',
    address: 'hi@lab100.org'
  },
  copyright:
    '© Icahn School of Medicine Mount Sinai 2019<br/>© Precision Wellness 2019'
};

// reports and assessments
const demographics = { title: 'Demographics', icon: { src: DemographicsIcon } };
const bodyComposition = {
  title: 'Body Composition',
  icon: { src: BodyCompositionIcon }
};
const bodySymmetry = {
  title: 'Body Symmetry',
  icon: { src: BodySymmetryIcon }
};
const cognition = { title: 'Cognition', icon: { src: CognitionIcon } };
const laboratory = { title: 'Laboratory', icon: { src: LaboratoryIcon } };
const dexterity = { title: 'Dexterity', icon: { src: DexterityIcon } };
const physicalActivity = {
  title: 'Physical Activity',
  icon: { src: PhysicalActivityIcon }
};
const strength = { title: 'Strength', icon: { src: StrengthIcon } };
const balance = { title: 'Balance', icon: { src: BalanceIcon } };
const anthropometrics = {
  title: 'Anthropometrics',
  icon: { src: AnthropometricsIcon }
};
const nutrition = { title: 'Nutrition', icon: { src: NutritionIcon } };

const medicalHistory = { title: 'Medical History' };
const medications = { title: 'Medications' };
const familyHistory = { title: 'Family History' };
const socialHistory = { title: 'Social History' };
const sleep = { title: 'Sleep' };
const mentalHealth = { title: 'Mental Health' };

export const reportSubitems = {
  demographics,
  bodyComposition,
  bodySymmetry,
  cognition,
  laboratory,
  dexterity,
  physicalActivity,
  strength,
  balance,
  anthropometrics,
  nutrition
};

export const assessmentSubitems = {
  medicalHistory,
  medications,
  familyHistory,
  socialHistory,
  sleep,
  physicalActivity,
  nutrition,
  mentalHealth
};

const historySubItems = {
  assessments: {
    title: 'Pre-Visit Assessments',
    subItems: assessmentSubitems
  }
};

export const nav = {
  items: {
    upcomingVisit: {
      title: 'Schedule a Visit',
      route: '/upcoming',
      icon: { src: UpcomingIcon },
      onClick: () => dispatch(highlightNavbarSection('upcomingVisit'))
    },
    currentReport: {
      title: 'Current Report',
      icon: { src: CurrentIcon },
      subItems: reportSubitems,
      onClick: () => dispatch(highlightNavbarSection('currentReport'))
    },
    history: {
      title: 'Past Visits',
      route: '/visits',
      icon: { src: HistoryIcon },
      subItems: historySubItems,
      onClick: () => dispatch(highlightNavbarSection('history'))
    },
    updateProfile: {
      title: 'Update Profile',
      route: '/profile',
      icon: { src: ProfileIcon },
      isMobileOnly: true
    },
    logout: {
      title: 'Logout',
      route: '/logout',
      icon: { src: LogoutIcon },
      isMobileOnly: true
    }
  }
};

const consentSubItems = [
  {
    route: '/sharing',
    title: 'Sharing Your Health Data'
  },
  {
    route: '/benefits',
    title: 'Benefits of Participation'
  },
  {
    route: '/risks',
    title: 'Potential Risks of Participation'
  },
  {
    route: '/privacy',
    title: 'Data Privacy and Security'
  },
  {
    route: '/withdrawing',
    title: 'Withdrawing from Research'
  },
  {
    route: '/review',
    title: 'Consent Review'
  }
];

export const consentNav = {
  title: 'Consent for Research',
  icon: { src: ConsentIcon },
  subItems: consentSubItems,
  isDisabled: true
};

export const schedule = {
  defaultTimes: ['9:00 am', '10:30 am', '1:00 pm', '2:30 pm', '4:00 pm'],
  onSelection: timeslot => {
    dispatch(selectTimeslot(timeslot));
  }
};

export const hamburgerMenu = {
  onClick: () => dispatch(toggleMenu())
};

export const profileMenu = {
  icon: { src: ProfileIcon },
  onLogoutClick: () => dispatch(changeRoute({ route: '/logout' }))
};

export const helpfulTools = {
  assessments: {
    title: 'Pre-Visit Assessments',
    icon: { text: '0/8 completed' }
  },
  directions: {
    title: 'Get Directions',
    icon: { src: DownCarot },
    accordianContent: `
  <p>
  <b>Lab100</b><br />
  1468 Madison Ave<br /><br />
  11th Floor - Levy Library<br />
  Entrances at E. 101st &amp; 5th and E. 100th &amp; Madison<br /><br />
  <a
    href="https://www.google.com/maps/place/1468+Madison+Ave,+New+York,+NY+10029/@40.7897313,-73.9548871,17z/data=!3m1!4b1!4m5!3m4!1s0x89c258a75b7d544f:0xa7072fa94ef9dfc5!8m2!3d40.7897313!4d-73.9526931"
    target="_blank"
    rel="noopener noreferrer"
  >
    View on Google Maps >
  </a>
</p>
`
  },
  whatToExpect: {
    title: 'What To Expect',
    icon: { src: DownCarot },
    accordianContent: `
<p>
  The entire visit will last up to 90 minutes. We will
  measure your vital signs and perform a standard blood draw
  (fasting is not required). We will also perform a 3D body
  surface scan, body composition analysis, cognitive
  battery, as well as assessments of strength, dexterity,
  and balance. We will explain these tests to you as we go,
  and we will discuss all of your results with you at the
  end of your visit.
</p>
`
  },
  whatToWear: {
    title: 'What to Wear',
    icon: { src: DownCarot },
    accordianContent: `
<p>
  Wear or bring comfortable clothing to change into. You
  will need to roll up your sleeves for bloodwork and vital
  signs. For the 3D body surface scan, you will be asked to
  change into your underwear or other form-fitting clothing
  (sports-bra, spandex, or swimsuit). Loose fitting
  clothing, including boxer shorts, may distort your
  results. If you wear reading glasses please bring them
  with you for your visit.
</p>
`
  },
  contact: {
    title: 'Contact Us',
    icon: { src: DownCarot },
    accordianContent: `
<p>
  We are here to answer any questions that may arise as you
  prepare for your visit. Please feel free to contact us.
<br/>
<br/>
  Email:
  <a href="mailto:support@lab100.org">
    support@lab100.org
  </a>
</p>
`
  }
};

document.title = app.title;

export default {
  app,
  nav,
  footer,
  header,
  terms,
  desktopHeader,
  mobileHeader,
  profileMenu,
  helpfulTools,
  hamburgerMenu,
  reports,
  assessments,
  schedule,
  payment,
  consentNav,
  reportSubitems,
  assessmentSubitems
};
